import React, { useState } from 'react';
import { Modal, Button, Row, Col, ProgressBar, Badge, Spinner, Alert } from 'react-bootstrap';

const AdminSubscriptionDetailModal = ({ 
  show, 
  onHide, 
  subscription, 
  metalPrices, 
  onPause, 
  onResume 
}) => {
  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  if (!subscription) return null;

  const {
    id,
    user_name,
    user_email,
    metal_type, 
    amount_per_period,
    weight,
    weight_type = 'oz',
    period, 
    current_balance,
    status,
    next_billing_date,
    created_at
  } = subscription;

  // Calculate progress towards delivery target
  const calculateProgress = () => {
    if (!metalPrices || !metalPrices[metal_type] || !weight) {
      return {
        progressPercentage: 0,
        remaining: '0.00',
        targetValue: '0.00',
        currentWeight: 0
      };
    }

    const priceData = metalPrices[metal_type];
    
    // Use the correct price based on weight_type
    let pricePerUnit;
    if (weight_type === 'g') {
      pricePerUnit = priceData.price_per_gram;
    } else if (weight_type === 'kg') {
      pricePerUnit = priceData.price_per_gram * 1000;
    } else {
      pricePerUnit = priceData.price_per_ounce;
    }
    
    // Calculate monetary target: (weight * price per unit) + 15%
    const baseTarget = weight * pricePerUnit;
    const targetPrice = baseTarget * 1.15;
    
    // Calculate progress as percentage of monetary value
    const progressPercentage = Math.max(0, Math.min(100, (current_balance / targetPrice) * 100));
    
    // Calculate remaining amount in dollars
    const remainingValue = Math.max(0, targetPrice - current_balance);
    
    // Calculate equivalent weight for display
    const currentWeightAmount = current_balance / pricePerUnit;
    
    return {
      progressPercentage: progressPercentage,
      remaining: remainingValue.toFixed(2),
      targetValue: targetPrice.toFixed(2),
      currentWeight: currentWeightAmount
    };
  };

  const progress = calculateProgress();

  // Get status color
  const getStatusColor = () => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Handle admin actions
  const handleAction = async (actionFn, actionName) => {
    setLoading(true);
    setActionType(actionName);
    setError(null);
    setSuccess(null);

    try {
      await actionFn(subscription.id);
      setSuccess(`Subscription ${actionName}d successfully.`);
      // Close modal after success
      setTimeout(() => {
        onHide();
      }, 1500);
    } catch (err) {
      setError(err.message || `Failed to ${actionName} subscription.`);
    } finally {
      setLoading(false);
      setActionType(null);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Subscription #{id} - {metal_type?.charAt(0).toUpperCase() + metal_type?.slice(1)}
          <span className={`badge bg-${getStatusColor()} ms-2`}>
            {status?.charAt(0).toUpperCase() + status?.slice(1)}
          </span>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
            {error && (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}
            
            {success && (
              <div className="alert alert-success">
                <i className="bi bi-check-circle me-2"></i>
                {success}
              </div>
            )}

            {/* User Information */}
            <div className="row mb-4">
              <div className="col-12">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="bi bi-person me-2"></i>
                  User Information
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-2">
                      <strong>Name:</strong> {user_name || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Email:</strong> {user_email}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-2">
                      <strong>Subscription ID:</strong> #{id}
                    </div>
                    <div className="mb-2">
                      <strong>Created:</strong> {formatDate(created_at)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Details */}
            <div className="row mb-4">
              <div className="col-12">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="bi bi-credit-card me-2"></i>
                  Subscription Details
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-2">
                      <strong>Metal Type:</strong> {metal_type?.charAt(0).toUpperCase() + metal_type?.slice(1)}
                    </div>
                    <div className="mb-2">
                      <strong>Amount per {period}:</strong> ${amount_per_period}
                    </div>
                    <div className="mb-2">
                      <strong>Target Weight:</strong> {weight} {weight_type}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-2">
                      <strong>Current Balance:</strong> ${current_balance?.toFixed ? current_balance.toFixed(2) : current_balance}
                      {progress.currentWeight !== undefined && (
                        <span className="text-muted"> ({progress.currentWeight?.toFixed(4)} {weight_type})</span>
                      )}
                    </div>
                    <div className="mb-2">
                      <strong>Next Billing:</strong> {formatDate(next_billing_date)}
                    </div>
                    <div className="mb-2">
                      <strong>Status:</strong> 
                      <span className={`badge bg-${getStatusColor()} ms-2`}>
                        {status?.charAt(0).toUpperCase() + status?.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Section */}
            <div className="row mb-3">
              <div className="col-12">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="bi bi-graph-up me-2"></i>
                  Progress to Target
                </h6>
                <div className="progress mb-2" style={{ height: '20px' }}>
                  <div 
                    className={`progress-bar ${metal_type === 'gold' ? 'bg-warning' : 'bg-primary'}`}
                    role="progressbar" 
                    style={{ width: `${progress.progressPercentage}%` }}
                  >
                    {progress.progressPercentage?.toFixed(1)}%
                  </div>
                </div>
                <div className="d-flex justify-content-between">
                  <small className="text-muted">
                    Target Value: ${progress.targetValue} (includes 15% premium)
                  </small>
                  <small className="text-muted">
                    Remaining: ${progress.remaining}
                  </small>
                </div>
              </div>
            </div>
      </Modal.Body>
      
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {status === 'active' && (
              <Button 
                variant="warning"
                size="sm"
                onClick={() => handleAction(onPause, 'pause')}
                disabled={loading && actionType === 'pause'}
              >
                {loading && actionType === 'pause' ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Pausing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-pause-circle me-2"></i>
                    Pause
                  </>
                )}
              </Button>
            )}
            
            {status === 'paused' && (
              <Button 
                variant="success"
                size="sm"
                onClick={() => handleAction(onResume, 'resume')}
                disabled={loading && actionType === 'resume'}
              >
                {loading && actionType === 'resume' ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Resuming...
                  </>
                ) : (
                  <>
                    <i className="bi bi-play-circle me-2"></i>
                    Resume
                  </>
                )}
              </Button>
            )}
            
            {status === 'cancelled' && (
              <div className="text-muted">
                <i className="bi bi-info-circle me-2"></i>
                This subscription has been cancelled and cannot be modified.
              </div>
            )}
          </div>
          <Button variant="secondary" size="sm" onClick={onHide}>
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AdminSubscriptionDetailModal;